/**
 * Marketing API client.
 *
 * Mirrors apps/api/src/modules/crm-marketing. The audience shape is the same
 * object the register list page already produces (lib/audienceFilter.ts), which
 * is the whole reason that filter was lifted out of the page: a campaign stores
 * it, and "these 4 000 companies" means the same thing on both screens.
 */
import { api } from "./api";
import type { AudienceParams } from "./audienceFilter";

export type CampaignStatus = "draft" | "scheduled" | "active" | "paused" | "done";

/**
 * The stored audience. `register` reads the business register mirror,
 * `scanLeads` the site scanner's qualified leads - and those count different
 * things, companies versus domains.
 *
 * `AudienceParams`, not the form's `AudienceFilter`: the form state carries the
 * `ANY` sentinel and real booleans, and the server schema takes neither. Send
 * the form through `audienceToParams` and it is this shape; the schema is
 * `.strict()`, so getting that wrong is a 400 rather than a filter that quietly
 * selects nothing.
 */
export interface RegisterAudienceFilter extends AudienceParams {
  excludeFreemail?: boolean;
  excludeRoleAccounts?: boolean;
}

export interface ScanAudienceFilter {
  severity?: ("hot" | "warm" | "cool")[];
  status?: string[];
  scoreMin?: number;
  scoreMax?: number;
  cms?: string[];
  cmsNot?: string[];
  versionGap?: string[];
  hasEmail?: boolean;
  excludeFreemail?: boolean;
  excludeRoleAccounts?: boolean;
}

export type Audience =
  | { source: "register"; filter: RegisterAudienceFilter }
  | { source: "scanLeads"; filter: ScanAudienceFilter };

export interface CampaignRow {
  id: string;
  slug: string;
  name: string;
  status: CampaignStatus;
  channel: string;
  locale: string;
  subject: string;
  audienceSource: string;
  audienceCount: number | null;
  audienceCountedAt: string | null;
  autoEnabled: boolean;
  startsAt: string | null;
  endsAt: string | null;
  queued: number;
  sent: number;
  failed: number;
  skipped: number;
  replied: number;
  bounced: number;
  /** Spam reports. Its own number, never folded into `unsubscribed`. */
  complained: number;
  unsubscribed: number;
  createdAt: string;
  updatedAt: string;
}

export interface Campaign extends CampaignRow {
  description: string | null;
  preheader: string;
  bodyHtml: string;
  bodyText: string;
  /** The letter builder's block layout, when the letter was built rather than
   *  typed. `bodyHtml` is what gets SENT; this is what the builder re-opens.
   *  Null means the HTML is hand-written and the builder does not own it. */
  design: unknown | null;
  templateId: string | null;
  fromName: string;
  fromAddress: string;
  replyTo: string;
  audience: Audience | null;
  perRunCap: number;
  dailyCap: number;
  minGapSeconds: number;
  recontactDays: number;
  dedupeScope: "email" | "domain" | "company";
  skipShared: boolean;
  sharedAddressThreshold: number;
  quietFromHour: number;
  quietToHour: number;
  timezone: string;
}

/** Every reason a candidate can be dropped. The API guarantees these sum with
 *  `sendable` back to `examined` - an audience that shrinks with rows
 *  unaccounted for is the thing the module exists to prevent. */
export type SkipReason =
  | "duplicate-email" | "shared-address" | "suppressed" | "cooldown"
  | "no-email" | "invalid-email" | "freemail" | "role-account"
  | "natural-person" | "not-lawful" | "no-mx" | "already-client" | "cap";

export interface AudiencePreview {
  unit: "company" | "domain";
  total: number;
  examined: number;
  partial: boolean;
  uniqueAddresses: number;
  sendable: number;
  skipped: Record<SkipReason, number>;
  freemail: number;
  roleAccounts: number;
  unknownPersonType: number;
  /** Sendable rows whose letter must carry the article 14 and 21 blocks. */
  needsPrivacyNotice: number;
  sample: { ref: string; companyName: string | null; email: string; freemail: boolean; role: boolean }[];
}

export interface RecipientRow {
  id: string;
  regCode: string | null;
  domain: string | null;
  email: string;
  companyName: string | null;
  status: string;
  skipReason: SkipReason | null;
  skippedFor: string | null;
  messageId: string | null;
  threadId: string | null;
  attempts: number;
  error: string | null;
  queuedAt: string;
  sentAt: string | null;
  failedAt: string | null;
  /** When the provider last said what became of this letter. */
  eventAt: string | null;
}

export interface SuppressionRow {
  id: string;
  kind: "email" | "domain";
  value: string;
  source: string;
  reason: string | null;
  note: string | null;
  createdAt: string;
}

const BASE = "/admin/crm/marketing";

export const listCampaigns = () => api.get<{ rows: CampaignRow[] }>(`${BASE}/campaigns`);

export const getCampaign = (id: string) =>
  api.get<{
    campaign: Campaign;
    skipReasons: Partial<Record<SkipReason, number>>;
    /** One count per recipient status, counted from the ledger rather than from
     *  the campaign's cached columns. The detail screen shows these because a
     *  breakdown by status is what it is for; the cached columns exist so the
     *  LIST can render twenty campaigns from one query. */
    statusCounts: Record<string, number>;
    /** Threads in the shared inbox that this campaign started. */
    replyThreads: number;
  }>(`${BASE}/campaigns/${encodeURIComponent(id)}`);

export const createCampaign = (body: Record<string, unknown>) =>
  api.post<{ campaign: Campaign }>(`${BASE}/campaigns`, body);

export const patchCampaign = (id: string, body: Record<string, unknown>) =>
  api.patch<{ campaign: Campaign }>(`${BASE}/campaigns/${encodeURIComponent(id)}`, body);

/** Pause and resume live on their own narrow route: the body carries a status
 *  and nothing else, so this can never be the call that wipes a campaign. */
export const setCampaignStatus = (id: string, status: CampaignStatus) =>
  api.post<{ campaign: CampaignRow }>(`${BASE}/campaigns/${encodeURIComponent(id)}/status`, { status });

export const deleteCampaign = (id: string) =>
  api.del<{ ok: true }>(`${BASE}/campaigns/${encodeURIComponent(id)}`);

export interface RecipientQuery {
  status?: string;
  skipReason?: string;
  q?: string;
  page?: number;
  pageSize?: number;
}

export function listRecipients(id: string, params: RecipientQuery = {}) {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "" && v !== null) qs.set(k, String(v));
  }
  return api.get<{ rows: RecipientRow[]; total: number; page: number; pageSize: number; pageCount: number }>(
    `${BASE}/campaigns/${encodeURIComponent(id)}/recipients?${qs.toString()}`,
  );
}

/**
 * Enrolment: the audience, written into the ledger.
 *
 * Sends nothing, may be run on a draft, and may be run twice - the second run
 * over an unchanged audience creates nothing and says so. `dryRun` answers the
 * same question without writing, which is what the button asks first.
 */
export interface EnrolOutcome {
  campaignId: string;
  dryRun: boolean;
  /** Rows matching the audience in the whole table. */
  total: number;
  /** Rows this pass examined. Below `total` when the audience is larger. */
  examined: number;
  partial: boolean;
  created: { pending: number; skipped: number };
  /** Rows the ledger already held. */
  alreadyThere: number;
  /** Companies that share a mailbox with a row already in the ledger. */
  collapsed: number;
  /** No usable address at all, by reason. Cannot be stored. */
  unstorable: Partial<Record<SkipReason, number>>;
  /** Held back for a reason that passes on its own, by reason. Not stored. */
  deferred: Partial<Record<SkipReason, number>>;
  contactsCreated: number;
  ledger: Record<string, number>;
  runId: string | null;
}

export function enrolCampaign(id: string, body: { limit?: number; dryRun?: boolean } = {}) {
  return api.post<EnrolOutcome>(`${BASE}/campaigns/${encodeURIComponent(id)}/enrol`, body);
}

/**
 * Starting one batch.
 *
 * The route answers before the batch is over - fifty letters thirty seconds
 * apart is twenty-five minutes - so a 202 means "it began", not "it finished".
 * Everything that could refuse it is known in milliseconds and comes back as a
 * named reason, which is what the panel shows instead of a hopeful spinner.
 */
export interface RunStarted {
  runId: string;
  /** How many letters this batch may send, after both caps. */
  budget: number;
}

export function startCampaignRun(id: string) {
  return api.post<RunStarted>(`${BASE}/campaigns/${encodeURIComponent(id)}/send`, {});
}

export interface MarketingRun {
  id: string;
  campaignId: string | null;
  trigger: string;
  startedAt: string;
  finishedAt: string | null;
  picked: number;
  sent: number;
  skipped: number;
  failed: number;
  error: string | null;
}

/**
 * The circuit breaker, as the panel sees it.
 *
 * Mirrors `crm-marketing/lib/send/breaker.ts`. `bounceMeasurable` and
 * `complaintMeasurable` are the part that is easy to drop and must not be: below
 * the minimum sample the server does NOT act on the rate, and a screen that
 * printed "0.0%" for a campaign that has sent nine letters would be reporting a
 * clean record where there is simply no record.
 */
export interface BreakerRates {
  sent: number;
  bounced: number;
  complained: number;
  bounceRate: number;
  complaintRate: number;
  bounceMeasurable: boolean;
  complaintMeasurable: boolean;
}

export interface BreakerTrip {
  scope: "campaign" | "domain";
  reason: "bounce" | "complaint";
  rate: number;
  limit: number;
}

export interface BreakerAck {
  until: string;
  bounceRate: number;
  complaintRate: number;
  by: string | null;
  at: string;
}

export interface BreakerView {
  /** Null when a batch may run. */
  trip: BreakerTrip | null;
  campaign: BreakerRates;
  domain: BreakerRates;
  /** A live acknowledgement is holding a trip back. Not the same as clean. */
  waived: boolean;
  windowDays: number;
  /** The thresholds themselves, sent by the server rather than restated here. */
  limits: { bounce: number; complaint: number };
  ack: BreakerAck | null;
}

export function listCampaignRuns(id: string) {
  return api.get<{ runs: MarketingRun[]; breaker: BreakerView }>(
    `${BASE}/campaigns/${encodeURIComponent(id)}/runs`,
  );
}

/**
 * Accept the current rates for a while.
 *
 * The body carries only the duration. What is being accepted is measured on the
 * server at the moment of the click, so the browser cannot understate it.
 */
export function ackBreaker(campaignId: string, hours = 24) {
  return api.post<{ ack: BreakerAck }>(
    `${BASE}/breaker/ack?campaignId=${encodeURIComponent(campaignId)}`,
    { hours },
  );
}

export const clearBreakerAck = () => api.del<{ ok: true }>(`${BASE}/breaker/ack`);

export interface TestLetterSent {
  ok: true;
  to: string;
  /** Set when test mode moved the letter to the test inbox. */
  redirectedFrom: string | null;
  messageId: string | null;
}

/** One letter to an address the operator names. Writes nothing to the ledger. */
export function sendTestLetter(id: string, body: { to: string; legalState?: string }) {
  return api.post<TestLetterSent>(`${BASE}/campaigns/${encodeURIComponent(id)}/test`, body);
}

export interface PreviewBody {
  audience: Audience;
  recontactDays?: number;
  skipShared?: boolean;
  sharedAddressThreshold?: number;
  excludeNaturalPersons?: boolean;
  excludeUnknownPersonType?: boolean;
  maxSendable?: number;
  limit?: number;
}

export const previewAudience = (body: PreviewBody) =>
  api.post<AudiencePreview>(`${BASE}/audience/preview`, body);

/**
 * How much of the register mirror each selectable column actually covers.
 *
 * The audience form reads this so it can say "this filter would select nobody,
 * and here is why" instead of quietly returning zero. Three of its filters read
 * columns that only the `tax` and `reports` sync datasets populate, and those
 * have never run in production - so without this the form offers filters that
 * cannot match a single row.
 */
export interface RegisterCoverage {
  /** Active companies in the mirror. The denominator for the rest. */
  total: number;
  withEmail: number;
  /** EMTA revenue - what the revenue range filter reads. */
  withRevenue: number;
  /** EMTA headcount - what the employee range filter reads. */
  withEmployees: number;
  /** Annual-report net profit - what the profitability filter reads. */
  withReport: number;
}

export const getRegisterCoverage = () => api.get<RegisterCoverage>(`${BASE}/audience/coverage`);

export const listSharedAddresses = (minCompanies = 5, limit = 50) =>
  api.get<{ rows: { email: string; companies: number }[]; minCompanies: number }>(
    `${BASE}/audience/shared?minCompanies=${minCompanies}&limit=${limit}`,
  );

export interface SuppressionQuery {
  q?: string;
  kind?: "email" | "domain";
  source?: string;
  page?: number;
  pageSize?: number;
}

export function listSuppression(params: SuppressionQuery = {}) {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "" && v !== null) qs.set(k, String(v));
  }
  return api.get<{ rows: SuppressionRow[]; total: number; page: number; pageSize: number; pageCount: number }>(
    `${BASE}/suppression?${qs.toString()}`,
  );
}

export const addSuppression = (body: {
  kind: "email" | "domain";
  value: string;
  /** "unsubscribe" when a person asked us to stop, which is what a reply in the
   *  inbox usually is. That entry cannot be deleted again; "manual" can. */
  source?: "manual" | "unsubscribe";
  reason?: string;
  note?: string;
}) => api.post<{ suppression: SuppressionRow }>(`${BASE}/suppression`, body);

export const removeSuppression = (id: string) =>
  api.del<{ ok: true }>(`${BASE}/suppression/${encodeURIComponent(id)}`);

/** Skip reasons in the order the UI lists them: the ones an operator can act on
 *  first, the mechanical ones last. */
export const SKIP_REASON_ORDER: SkipReason[] = [
  "suppressed",
  "natural-person",
  "not-lawful",
  "shared-address",
  "cooldown",
  "duplicate-email",
  "freemail",
  "role-account",
  "no-mx",
  "already-client",
  "no-email",
  "invalid-email",
  "cap",
];
